// src/components/store/NewRefillsComponents/RefillReviewStep.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Package,
  Calendar,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Droplets,
  Loader,
} from "lucide-react";
import { useGoogleMaps } from "../../../hooks/useGoogleMaps";
import primaryCoveragePolygon from "../../pages/coverageComponents/perimeter.json";
import Badge from "../QuickAddressComponents/Badge";
import DividerComponent from "../../ui/DividerComponent";

const RefillReviewStep = ({
  groupedByAddress = {},
  totals = {},
  selectedDeliveryDate,
  selectedTimeSlot,
  onTotalsUpdate, // Callback para actualizar totales en el componente padre
}) => {
  const [addressValidation, setAddressValidation] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  const { validateLocation } = useGoogleMaps(primaryCoveragePolygon, true);

  // Función para obtener información de zona y costo
  const getZoneInfo = useCallback((zone) => {
    switch (zone) {
      case 1:
        return {
          cost: 0,
          text: "Gratis",
          color: "text-green-700",
          bgColor: "bg-green-50",
        };
      case 2:
        return {
          cost: 3500,
          text: "$3.500",
          color: "text-yellow-700",
          bgColor: "bg-yellow-50",
        };
      case 3:
        return {
          cost: 7000,
          text: "$7.000",
          color: "text-orange-700",
          bgColor: "bg-orange-50",
        };
      default:
        return {
          cost: null,
          text: "Sin cobertura",
          color: "text-red-700",
          bgColor: "bg-red-50",
        };
    }
  }, []);

  // Función para formatear precio
  const formatPrice = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return "No seleccionada";

    const date = new Date(dateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return date.toLocaleDateString("es-ES", options);
  };

  // Función para validar una dirección específica
  const validateSingleAddress = useCallback(
    async (addressId, addressData) => {
      console.log(
        ">>>>>>>>>>>",
        `Validando dirección ${addressId}:`,
        addressData
      );
      try {
        // CORRECCIÓN: addressData ya es el array, no un objeto con items
        const firstItem = Array.isArray(addressData)
          ? addressData[0]
          : addressData;

        // Si ya tiene coordenadas guardadas, validar directamente
        if (firstItem?.latitude && firstItem?.longitude) {
          const coords = { lat: firstItem.latitude, lng: firstItem.longitude };
          console.log(
            `Validando dirección ${addressId} con coordenadas:`,
            coords
          );
          const validationResult = validateLocation(coords);
          const zoneInfo = getZoneInfo(validationResult.zone);

          return {
            hasDeliveryCost: validationResult.isValid,
            zone: validationResult.zone,
            cost: zoneInfo.cost,
            message: zoneInfo.text,
            coordinates: coords,
            isValid: validationResult.isValid,
            zoneInfo,
          };
        }

        // Si no tiene coordenadas, no podemos validar
        console.log(
          ">>>>>>>>>>>",
          `Dirección ${addressId} sin coordenadas - marcando como inválida`
        );

        return {
          hasDeliveryCost: false,
          zone: null,
          cost: null,
          message: "Sin coordenadas",
          coordinates: null,
          isValid: false,
          zoneInfo: getZoneInfo(null),
        };
      } catch (error) {
        console.error("Error validando dirección:", error);
        return {
          hasDeliveryCost: false,
          zone: null,
          cost: null,
          message: "Error al validar",
          coordinates: null,
          isValid: false,
          zoneInfo: getZoneInfo(null),
        };
      }
    },
    [validateLocation, getZoneInfo]
  );

  // Función para validar todas las direcciones
  const validateAllAddresses = useCallback(async () => {
    if (!validateLocation || Object.keys(groupedByAddress).length === 0) return;

    setIsValidating(true);
    const validationResults = {};

    try {
      // Procesar todas las direcciones en paralelo
      const validationPromises = Object.entries(groupedByAddress).map(
        async ([addressId, addressData]) => {
          const result = await validateSingleAddress(addressId, addressData);
          return { addressId, result };
        }
      );

      const results = await Promise.all(validationPromises);

      // Organizar resultados por addressId
      results.forEach(({ addressId, result }) => {
        validationResults[addressId] = result;
      });

      setAddressValidation(validationResults);
    } catch (error) {
      console.error("Error validando direcciones:", error);
    } finally {
      setIsValidating(false);
    }
  }, [groupedByAddress, validateSingleAddress]);

  // Efecto para iniciar validación cuando cambian las direcciones
  useEffect(() => {
    validateAllAddresses();
  }, [validateAllAddresses]);

  // Calcular totales completos incluyendo costos de entrega
  const calculatedTotals = useMemo(() => {
    const addresses = Object.keys(groupedByAddress);
    let itemCount = 0;
    let subtotal = 0;
    let deliveryCost = 0;

    // Calcular subtotal de productos
    addresses.forEach((addressId) => {
      const items = groupedByAddress[addressId] || [];
      const itemsArray = Array.isArray(items) ? items : [];
      itemsArray.forEach((item) => {
        itemCount += item.quantity || 1;
        subtotal += (item.price || 0) * (item.quantity || 1);
      });
    });

    // Calcular costos de entrega por dirección
    addresses.forEach((addressId) => {
      const validation = addressValidation[addressId];
      if (validation && validation.isValid && validation.cost !== null) {
        const items = groupedByAddress[addressId] || [];
        const itemsArray = Array.isArray(items) ? items : [];
        const jugsCount = itemsArray.reduce(
          (sum, item) => sum + (item.quantity || 1),
          0
        );

        // El costo se aplica por botellón (excepto zona 1 que es gratis)
        if (validation.zone !== 1) {
          deliveryCost += validation.cost * jugsCount;
        }
      }
    });

    const total = subtotal + deliveryCost;

    // En RefillReviewStep.jsx, modifica el useMemo de calculatedTotals:
    const finalTotals = {
      itemCount,
      addressCount: addresses.length,
      subtotal,
      deliveryCost,
      total,
      totalQuantity: itemCount,
      totalPrice: total,
      // AGREGAR: Información de validación por dirección
      addressValidation, // ← Esto es clave
      validatedAddresses: Object.keys(addressValidation).length,
      validAddresses: Object.values(addressValidation).filter(
        (v) => v && v.isValid
      ).length,
      invalidAddresses: Object.values(addressValidation).filter(
        (v) => v && !v.isValid
      ).length,
    };

    // Notificar al componente padre sobre los nuevos totales
    if (onTotalsUpdate) {
      onTotalsUpdate(finalTotals);
    }

    return finalTotals;
  }, [groupedByAddress, addressValidation, onTotalsUpdate]);

  // Estadísticas de validación
  const validationStats = useMemo(() => {
    const addressIds = Object.keys(groupedByAddress);
    const total = addressIds.length;
    const validated = Object.keys(addressValidation).length;
    const valid = Object.values(addressValidation).filter(
      (v) => v && v.isValid
    ).length;
    const invalid = Object.values(addressValidation).filter(
      (v) => v && !v.isValid
    ).length;

    return {
      total,
      validated,
      valid,
      invalid,
      isComplete: validated === total && total > 0,
    };
  }, [groupedByAddress, addressValidation]);

  const allValid = validationStats.isComplete && validationStats.invalid === 0;

  return (
    <div className="space-y-4 mt-8">

      {Object.entries(groupedByAddress).map(([addressId, items], index) => {
        const itemsArray = Array.isArray(items) ? items : [];
        const validation = addressValidation[addressId];
        const isValid = validation?.isValid;
        const deliveryCostForAddress =
          validation && validation.cost !== null
            ? validation.cost *
              itemsArray.reduce((sum, item) => sum + (item.quantity || 1), 0)
            : 0;

        return (
          <motion.div
            key={addressId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-2 sm:p-4 rounded-xl flex-col flex items-start gap-4 shadow-sm overflow-hidden bg-gray-50 ${
              validation && !isValid ? "border-red-300 border" : ""
            }`}
          >
            <div className="flex gap-2 items-center">
              {itemsArray[0].address_image_url ? (
                <div className="min-w-16 max-w-16 h-16 rounded-xl overflow-hidden justify-center items-center flex">
                  <img
                    src={itemsArray[0].address_image_url}
                    alt="Mapa de la dirección"
                    className="w-48 h-48 rounded-md mr-2 object-cover"
                  />
                </div>
              ) : (
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1 h-24 w-24 flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-blue-600" />
                </div>
              )}
              <div className="flex flex-col">
                <h3 className="text-gray-900 text-base md:text-lg line-clamp-2 font-medium">
                  {itemsArray[0]?.address || addressId}
                </h3>
                <p className="text-base text-gray-500">
                  {itemsArray[0]?.city || addressId}
                </p>
              </div>
            </div>
            <DividerComponent color="ebebeb" />
            <div className="flex-col flex md:flex-row gap-2 md:gap-3 ">
              <div className="flex items-center gap-2">
                <p className="text-sm md:text-base text-nowrap">
                  Recambios a realizar:
                </p>
                <Badge
                  text={
                    itemsArray.length === 1
                      ? itemsArray.length + " recambio"
                      : itemsArray.length + " recambios"
                  }
                  bgColor={"bg-gray-200"}
                />
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm md:text-base text-nowrap">
                  Costo de entrega:
                </p>
                {validation ? (
                  <Badge
                    text={
                      validation.zone === 1
                        ? "Gratis"
                        : validation.zone === 2
                        ? "$3.500 por botellón"
                        : "$7.000 por botellón"
                    }
                    zone={validation.zone}
                  />
                ) : (
                  <Loader className="animate-spin h-4 w-4 text-blue-600" />
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default RefillReviewStep;
